import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { Link, useParams } from 'react-router-dom';
import {
  ArrowRight,
  CircleAlert,
  Loader2,
  TrainFront,
  Wallet,
  Clock3,
  Info,
  Sparkles,
  Share2,
} from 'lucide-react';
import { CLOUD_FUNCTIONS_BASE_URL } from '../../auth/firebaseConfig';
import { DublinCommuterTradeoffReport } from '../../types/dublinCommuterTradeoff';
import PseoEmailCapture from '../../components/pseo/PseoEmailCapture';
import {
  FacebookShareButton,
  TwitterShareButton,
  WhatsappShareButton,
  LinkedinShareButton,
  FacebookIcon,
  TwitterIcon,
  WhatsappIcon,
  LinkedinIcon,
} from 'react-share';

const formatCurrency = (value: number) =>
  new Intl.NumberFormat('en-IE', {
    style: 'currency',
    currency: 'EUR',
    maximumFractionDigits: 0,
  }).format(value || 0);

const progressWidth = (value: number) => `${Math.max(6, Math.min(100, value))}%`;

const DublinCommuterComparisonPage: React.FC = () => {
  const { comparisonSlug } = useParams<{ comparisonSlug: string }>();
  const [data, setData] = useState<DublinCommuterTradeoffReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const normalizedSegment = String(comparisonSlug || '')
      .trim()
      .toLowerCase();
    if (!normalizedSegment) {
      setError('Comparison town is missing.');
      setLoading(false);
      return;
    }
    const derivedSlug = normalizedSegment.startsWith('renting-in-dublin-vs-')
      ? normalizedSegment
      : `renting-in-dublin-vs-${normalizedSegment}`;

    const fetchData = async () => {
      setLoading(true);
      setError('');
      try {
        const response = await fetch(
          `${CLOUD_FUNCTIONS_BASE_URL}/getDublinCommuterTradeoffReport?slug=${encodeURIComponent(
            derivedSlug
          )}`
        );
        const result = await response.json();
        if (!result.success || !result.data) {
          setError('Comparison report not found.');
          return;
        }
        setData(result.data as DublinCommuterTradeoffReport);
      } catch (e) {
        setError('Unable to load comparison report.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [comparisonSlug]);

  if (loading) {
    return (
      <div className="min-h-[70vh] flex flex-col items-center justify-center bg-slate-50">
        <Loader2 className="h-10 w-10 animate-spin text-emerald-600 mb-3" />
        <p className="font-bold text-slate-500">Loading comparison...</p>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="max-w-2xl mx-auto my-20 px-4">
        <div className="rounded-3xl border border-slate-200 bg-white p-10 text-center">
          <CircleAlert className="h-16 w-16 text-amber-500 mx-auto mb-6" />
          <h1 className="text-3xl font-black text-slate-900 mb-3">Comparison Unavailable</h1>
          <p className="text-slate-600 mb-8">{error}</p>
          <Link
            to="/compare/renting-in-dublin-vs-commuter-towns"
            className="inline-block rounded-2xl bg-emerald-600 text-white px-8 py-4 font-black hover:bg-emerald-700"
          >
            Browse Commuter Comparisons
          </Link>
        </div>
      </div>
    );
  }

  const canonicalUrl = `https://rentsmart.ie/compare/${data.slug}`;
  const shareUrl = typeof window !== 'undefined' ? window.location.href : canonicalUrl;
  const shareText = `Dublin vs ${data.commuter_town.name}: save ${formatCurrency(
    data.calculations.net_monthly_gain
  )}/month after commute costs. Compare more relocation trade-offs on RentSmart.ie.`;

  return (
    <div className="relative min-h-screen w-screen max-w-none left-1/2 right-1/2 ml-[-50vw] mr-[-50vw] overflow-x-hidden bg-slate-50">
      <Helmet>
        <title>{data.seo_content.meta_title}</title>
        <meta name="description" content={data.seo_content.meta_desc} />
        <meta name="keywords" content={data.seo_content.keywords.join(', ')} />
        <link rel="canonical" href={canonicalUrl} />
        <script type="application/ld+json">{JSON.stringify(data.schema)}</script>
      </Helmet>

      <header className="bg-slate-900 text-white pt-10 pb-16 px-4 relative overflow-hidden">
        <div className="absolute -top-20 -right-16 h-64 w-64 rounded-full bg-emerald-500/20 blur-3xl" />
        <div className="absolute -bottom-20 -left-16 h-64 w-64 rounded-full bg-sky-500/20 blur-3xl" />
        <div className="relative max-w-7xl mx-auto">
          <div className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-3 py-1 text-xs font-black uppercase tracking-widest mb-4">
            <TrainFront className="h-4 w-4" /> Dublin Relocation Consultant View
          </div>
          <h1 className="text-4xl md:text-6xl font-black tracking-tight mb-3">
            {data.seo_content.h1}
          </h1>
          <p className="text-slate-300 text-lg max-w-3xl leading-relaxed">
            Moving to {data.commuter_town.name} saves you{' '}
            <strong>{formatCurrency(data.calculations.net_monthly_gain)}</strong> per month, even
            after commute costs.
          </p>
          <div className="mt-6 flex flex-wrap items-center gap-3">
            <span className="text-xs font-black uppercase tracking-widest text-slate-400">
              Share:
            </span>
            <FacebookShareButton url={shareUrl} hashtag="#RentSmartIE">
              <FacebookIcon size={30} round />
            </FacebookShareButton>
            <TwitterShareButton url={shareUrl} title={shareText}>
              <TwitterIcon size={30} round />
            </TwitterShareButton>
            <WhatsappShareButton url={shareUrl} title={shareText}>
              <WhatsappIcon size={30} round />
            </WhatsappShareButton>
            <LinkedinShareButton
              url={shareUrl}
              title={`Dublin vs ${data.commuter_town.name}`}
              summary={shareText}
              source="RentSmart.ie"
            >
              <LinkedinIcon size={30} round />
            </LinkedinShareButton>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 -mt-8 pb-16">
        <section className="rounded-[2.5rem] border border-slate-100 bg-white p-8 md:p-10 shadow-xl shadow-slate-200/40 mb-8 mt-16">
          <h2 className="text-2xl font-black text-slate-900 mb-5">Time Lost vs Money Gained</h2>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="rounded-3xl border border-emerald-200 bg-emerald-50 p-6">
              <p className="text-[10px] uppercase tracking-widest font-black text-emerald-700 mb-1">
                Monthly Money Gained
              </p>
              <p className="text-4xl font-black text-emerald-900">
                {formatCurrency(data.calculations.net_monthly_gain)}
              </p>
              <div className="mt-4 h-2 rounded-full bg-emerald-100">
                <div
                  className="h-2 rounded-full bg-emerald-600"
                  style={{ width: progressWidth(data.tradeoff_visual.money_gained_score) }}
                />
              </div>
            </div>
            <div className="rounded-3xl border border-sky-200 bg-sky-50 p-6">
              <p className="text-[10px] uppercase tracking-widest font-black text-sky-700 mb-1">
                Daily Time Cost
              </p>
              <p className="text-4xl font-black text-sky-900">
                {data.calculations.commute_minutes_one_way} min
              </p>
              <div className="mt-4 h-2 rounded-full bg-sky-100">
                <div
                  className="h-2 rounded-full bg-sky-600"
                  style={{ width: progressWidth(data.tradeoff_visual.time_lost_score) }}
                />
              </div>
            </div>
          </div>
        </section>

        <section className="grid lg:grid-cols-12 gap-8 mb-8">
          <div className="lg:col-span-8 space-y-8">
            <div className="rounded-[2.5rem] border border-slate-100 bg-white p-8 md:p-10 shadow-sm">
              <h2 className="text-2xl font-black text-slate-900 mb-4">Lifestyle Swap</h2>
              <p className="text-slate-700 font-semibold mb-4 leading-relaxed">
                For the price of a studio in Dublin 8 (
                {formatCurrency(data.lifestyle_swap.dublin_reference_rent)}), in{' '}
                {data.commuter_town.name} you typically get a {data.lifestyle_swap.town_equivalent}.
              </p>
              <div className="rounded-2xl border border-slate-200 bg-slate-50 p-5 text-sm text-slate-700 font-medium leading-relaxed">
                {data.lifestyle_swap.narrative}
              </div>
            </div>

            <div className="rounded-[2.5rem] border border-slate-100 bg-white p-8 md:p-10 shadow-sm">
              <h2 className="text-2xl font-black text-slate-900 mb-4">TFI Transport Deep-Dive</h2>
              <div className="grid md:grid-cols-2 gap-4 mb-5">
                <div className="rounded-2xl border border-slate-200 bg-slate-50 p-5">
                  <p className="text-[10px] uppercase tracking-wider font-black text-slate-500 mb-1">
                    Primary Route
                  </p>
                  <p className="text-lg font-black text-slate-900">{data.transport.primary_mode}</p>
                  <p className="text-xs font-semibold text-slate-500 mt-1">
                    {data.transport.notes[0]}
                  </p>
                </div>
                <div className="rounded-2xl border border-slate-200 bg-slate-50 p-5">
                  <p className="text-[10px] uppercase tracking-wider font-black text-slate-500 mb-1">
                    Last Service from Dublin
                  </p>
                  <p className="text-lg font-black text-slate-900">
                    {data.transport.last_service_from_dublin}
                  </p>
                  <p className="text-xs font-semibold text-slate-500 mt-1">
                    Useful for evening social and late work patterns.
                  </p>
                </div>
              </div>

              <div className="space-y-3">
                <div className="flex items-center justify-between rounded-2xl border border-slate-200 bg-white p-4">
                  <span className="text-sm font-bold text-slate-600">Monthly commute cost</span>
                  <span className="text-sm font-black text-slate-900">
                    {formatCurrency(data.calculations.commute_cost_monthly)}
                  </span>
                </div>
                <div className="flex items-center justify-between rounded-2xl border border-slate-200 bg-white p-4">
                  <span className="text-sm font-bold text-slate-600">90-minute fare support</span>
                  <span className="text-sm font-black text-slate-900">
                    {data.transport.ninety_minute_fare_applicable ? 'Applies' : 'Not core fare'}
                  </span>
                </div>
                {data.transport.post_taxsaver_cost ? (
                  <div className="flex items-center justify-between rounded-2xl border border-emerald-200 bg-emerald-50 p-4">
                    <span className="text-sm font-bold text-emerald-700">
                      TaxSaver adjusted net cost
                    </span>
                    <span className="text-sm font-black text-emerald-900">
                      {formatCurrency(data.transport.post_taxsaver_cost)}
                    </span>
                  </div>
                ) : null}
              </div>
            </div>

            {data.ai_enrichment ? (
              <div className="rounded-[2.5rem] border border-slate-100 bg-white p-8 md:p-10 shadow-sm">
                <h2 className="text-2xl font-black text-slate-900 mb-4 flex items-center gap-2">
                  <Sparkles className="h-5 w-5 text-emerald-600" /> Relocation Strategy
                </h2>
                <div className="grid md:grid-cols-2 gap-4 mb-5">
                  <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-5">
                    <p className="text-[10px] uppercase tracking-widest font-black text-emerald-700 mb-2">
                      Verdict Context
                    </p>
                    <p className="text-sm text-emerald-900 font-semibold leading-relaxed">
                      {data.ai_enrichment.verdict_context}
                    </p>
                  </div>
                  <div className="rounded-2xl border border-sky-200 bg-sky-50 p-5">
                    <p className="text-[10px] uppercase tracking-widest font-black text-sky-700 mb-2">
                      Remote Work Fit
                    </p>
                    <p className="text-sm text-sky-900 font-semibold leading-relaxed">
                      {data.ai_enrichment.remote_work_fit}
                    </p>
                  </div>
                  <div className="rounded-2xl border border-violet-200 bg-violet-50 p-5">
                    <p className="text-[10px] uppercase tracking-widest font-black text-violet-700 mb-2">
                      Best Fit Renter
                    </p>
                    <p className="text-sm text-violet-900 font-semibold leading-relaxed">
                      {data.ai_enrichment.who_should_move}
                    </p>
                  </div>
                  <div className="rounded-2xl border border-rose-200 bg-rose-50 p-5">
                    <p className="text-[10px] uppercase tracking-widest font-black text-rose-700 mb-2">
                      Watch-Out Profile
                    </p>
                    <p className="text-sm text-rose-900 font-semibold leading-relaxed">
                      {data.ai_enrichment.who_should_avoid}
                    </p>
                  </div>
                </div>

                <div className="rounded-2xl border border-slate-200 bg-slate-50 p-5 mb-4">
                  <p className="text-[10px] uppercase tracking-widest font-black text-slate-600 mb-2">
                    Social-Life Trade-Off
                  </p>
                  <p className="text-sm text-slate-700 font-semibold leading-relaxed">
                    {data.ai_enrichment.social_life_impact}
                  </p>
                </div>

                {data.ai_enrichment.action_plan?.length ? (
                  <div className="rounded-2xl border border-slate-200 bg-white p-5 mb-4">
                    <p className="text-[10px] uppercase tracking-widest font-black text-slate-600 mb-3">
                      30-Day Action Plan
                    </p>
                    <div className="space-y-2">
                      {data.ai_enrichment.action_plan.map((step, index) => (
                        <div
                          key={`${step}-${index}`}
                          className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700 font-semibold"
                        >
                         {step}
                        </div>
                      ))}
                    </div>
                  </div>
                ) : null}
              </div>
            ) : null}

            <div className="rounded-[2.5rem] border border-slate-100 bg-white p-8 md:p-10 shadow-sm">
              <h2 className="text-2xl font-black text-slate-900 mb-4">FAQ</h2>
              <div className="space-y-3">
                {data.seo_content.faq.map((faq, index) => (
                  <details
                    key={`${faq.question}-${index}`}
                    className="rounded-2xl border border-slate-200 bg-slate-50 p-4"
                  >
                    <summary className="cursor-pointer font-bold text-slate-800">
                      {faq.question}
                    </summary>
                    <p className="mt-3 text-sm text-slate-600 leading-relaxed">{faq.answer}</p>
                  </details>
                ))}
              </div>
            </div>

            <PseoEmailCapture
              pageType="dublin-vs-commuter"
              pageSlug={data.slug}
              locationName={data.commuter_town.name}
              contextHeadline={`Commuter Trade-Off Updates For ${data.commuter_town.name}`}
              contextReason={`Get Irish market updates for Dublin vs ${data.commuter_town.name} relocators, including commute-cost trade-off shifts, rental pressure changes, and new comparison report drops.`}
            />
          </div>

          <aside className="lg:col-span-4">
            <div className="sticky top-8 space-y-6">
              <div className="rounded-3xl bg-gradient-to-br from-emerald-700 to-emerald-900 text-white p-6 shadow-xl shadow-emerald-900/20">
                <h3 className="text-xl font-black mb-2 flex items-center gap-2">
                  <Wallet className="h-5 w-5" /> Act on the Savings
                </h3>
                <p className="text-emerald-100/90 text-sm mb-5 leading-relaxed">
                  Compare live listings using the same budget frame used in this trade-off page.
                </p>
                <div className="space-y-3">
                  <a
                    href={data.ctas.rent_town.path}
                    className="no-underline flex items-center justify-between rounded-xl bg-white text-emerald-800 px-4 py-3 font-black text-sm hover:bg-emerald-50 transition-all"
                  >
                    {data.ctas.rent_town.label} <ArrowRight className="h-4 w-4" />
                  </a>
                  <a
                    href={data.ctas.rent_dublin.path}
                    className="no-underline flex items-center justify-between rounded-xl bg-white text-emerald-800 px-4 py-3 font-black text-sm hover:bg-emerald-50 transition-all"
                  >
                    {data.ctas.rent_dublin.label} <ArrowRight className="h-4 w-4" />
                  </a>
                </div>
              </div>

              {data.ctas.related_compares.length ? (
                <div className="rounded-3xl border border-slate-200 bg-white p-6">
                  <h3 className="text-xl font-black text-slate-900 mb-3 flex items-center gap-2">
                    <Clock3 className="h-5 w-5 text-sky-600" /> Related Town Plays
                  </h3>
                  <div className="space-y-2">
                    {data.ctas.related_compares.map((item) => (
                      <Link
                        key={item.path}
                        to={item.path}
                        className="no-underline flex items-center justify-between rounded-xl border border-slate-200 px-4 py-3 text-sm font-bold text-slate-700 hover:border-emerald-300 hover:text-emerald-700"
                      >
                        {item.label} <ArrowRight className="h-4 w-4" />
                      </Link>
                    ))}
                  </div>
                </div>
              ) : null}

              {data.ai_enrichment?.related_search_intents?.length ? (
                <div className="rounded-3xl border border-slate-200 bg-white p-6">
                  <h3 className="text-xl font-black text-slate-900 mb-3">
                    Popular Related Searches
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {data.ai_enrichment.related_search_intents.map((item, index) => (
                      <span
                        key={`${item}-${index}`}
                        className="inline-flex rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs font-bold text-slate-600"
                      >
                        {item}
                      </span>
                    ))}
                  </div>
                </div>
              ) : null}

              <div className="rounded-3xl border border-slate-200 bg-gradient-to-br from-white to-emerald-50 p-6">
                <h3 className="text-xl font-black text-slate-900 mb-2 flex items-center gap-2">
                  <Share2 className="h-5 w-5 text-emerald-600" /> Share RentSmart.ie
                </h3>
                <p className="text-sm text-slate-600 font-medium mb-4 leading-relaxed">
                  Help more renters discover RentSmart.ie by sharing this commuter trade-off page.
                </p>
                <div className="flex items-center gap-2">
                  <FacebookShareButton url={shareUrl} hashtag="#RentSmartIE">
                    <FacebookIcon size={32} round />
                  </FacebookShareButton>
                  <TwitterShareButton url={shareUrl} title={shareText}>
                    <TwitterIcon size={32} round />
                  </TwitterShareButton>
                  <WhatsappShareButton url={shareUrl} title={shareText}>
                    <WhatsappIcon size={32} round />
                  </WhatsappShareButton>
                  <LinkedinShareButton
                    url={shareUrl}
                    title={`Dublin vs ${data.commuter_town.name}`}
                    summary={shareText}
                    source="RentSmart.ie"
                  >
                    <LinkedinIcon size={32} round />
                  </LinkedinShareButton>
                </div>
              </div>
            </div>
          </aside>
        </section>

        <section className="rounded-2xl border border-amber-200 bg-amber-50 p-5">
          <div className="flex items-start gap-3">
            <Info className="h-5 w-5 text-amber-700 mt-0.5" />
            <p className="text-sm text-amber-900/90 font-semibold leading-relaxed">
              These are directional planning estimates for commuting renters. Final suitability
              depends on your exact schedule, route, and lease availability.
            </p>
          </div>
        </section>
      </main>
    </div>
  );
};

export default DublinCommuterComparisonPage;
